import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import * as authApi from "@/utils/authApi";
import type { UserInfo } from "@/utils/authApi";
import { getMySites, setCurrentSiteId } from "@/utils/api";

export interface User {
  id: string;
  username: string;
  nickname: string;
  avatar: string;
  phone?: string;
  wechat?: string;
  isAdmin?: boolean;
  isVip?: boolean;
  role?: string;
  created_at?: string;
  email?: string;
  // 所有自定义注册字段（动态，来自 user_meta 表）
  [key: string]: string | undefined | boolean | null;
}

interface AuthContextType {
  user: User | null;
  isLoggedIn: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  hasSiteAccess: boolean;  // 是否有站点管理权限
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string }>;
  register: (username: string, password: string, nickname: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  resetPassword: (username: string) => Promise<{ success: boolean; message?: string }>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasSiteAccess, setHasSiteAccess] = useState(false);

    // 检查登录状态
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const userInfo = await authApi.getCurrentUser();
      // 根据 role 判断是否是管理员: admin(平台管理员), tenant(代理商), site_admin(站点管理员)
      const isAdminByRole = ['admin', 'tenant', 'site_admin'].includes(userInfo.role);
      const baseUser = {
        id: userInfo.id,
        username: userInfo.username,
        nickname: userInfo.nickname,
        avatar: userInfo.avatar || '',
        phone: userInfo.phone,
        wechat: userInfo.wechat,
        isAdmin: isAdminByRole || userInfo.is_admin,
        isVip: userInfo.is_vip,
        role: userInfo.role,
        created_at: userInfo.created_at,
        email: userInfo.email,
      };
      // 透传所有来自 user_meta 的自定义字段（排除已知的标准字段）
      const knownKeys = ['id', 'username', 'nickname', 'avatar', 'phone', 'wechat', 'is_admin', 'is_vip', 'role', 'created_at', 'email'];
      const customFields: Record<string, string> = {};
      for (const [key, value] of Object.entries(userInfo)) {
        if (!knownKeys.includes(key) && typeof value === 'string') {
          customFields[key] = value;
        }
      }
      setUser({ ...baseUser, ...customFields });
      
      // 检查用户是否有站点访问权限
      try {
        const sitesRes = await getMySites();
        const sites = sitesRes.list || [];
        setHasSiteAccess(sites.length > 0);
        if (sites.length > 0) {
          const currentSiteId = localStorage.getItem('current_site_id');
          const hasAccess = currentSiteId && sites.some(s => s.id === currentSiteId);
          if (!hasAccess) {
            const preferred = sites.find(s => s.role === 'owner') || sites[0];
            setCurrentSiteId(preferred.id);
            localStorage.setItem('current_site_name', preferred.name);
          }
        }
      } catch (e) {
        setHasSiteAccess(false);
      }
    } catch (error) {
      // Token 无效，清除
      localStorage.removeItem('token');
      setUser(null);
      setHasSiteAccess(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 初始化时检查登录状态
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (username: string, password: string) => {
    try {
      const response = await authApi.login({ username, password });
      
      // 保存 Token
      localStorage.setItem('token', response.token);
      
      // 保存用户信息
      const userInfo = response.user;
      // 根据 role 判断是否是管理员: admin(平台管理员), tenant(代理商), site_admin(站点管理员)
      const isAdminByRole = ['admin', 'tenant', 'site_admin'].includes(userInfo.role);
      const baseUser = {
        id: userInfo.id,
        username: userInfo.username,
        nickname: userInfo.nickname,
        avatar: userInfo.avatar || '',
        phone: userInfo.phone,
        wechat: userInfo.wechat,
        isAdmin: isAdminByRole || userInfo.is_admin,
        isVip: userInfo.is_vip,
        role: userInfo.role,
        created_at: userInfo.created_at,
        email: userInfo.email,
      };
      // 透传所有来自 user_meta 的自定义字段
      const knownKeys = ['id', 'username', 'nickname', 'avatar', 'phone', 'wechat', 'is_admin', 'is_vip', 'role', 'created_at', 'email'];
      const customFields: Record<string, string> = {};
      for (const [key, value] of Object.entries(userInfo)) {
        if (!knownKeys.includes(key) && typeof value === 'string') {
          customFields[key] = value;
        }
      }
      const user: User = { ...baseUser, ...customFields };
      
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      
      // 登录成功后，获取用户可管理的站点并自动设置
      try {
        const sitesRes = await getMySites();
        const sites = sitesRes.list || [];
        setHasSiteAccess(sites.length > 0);
        if (sites.length > 0) {
          const currentSiteId = localStorage.getItem('current_site_id');
          const hasAccess = currentSiteId && sites.some(s => s.id === currentSiteId);
          if (!hasAccess) {
            // 选择 owner 角色的站点优先，否则选第一个
            const preferred = sites.find(s => s.role === 'owner') || sites[0];
            setCurrentSiteId(preferred.id);
            localStorage.setItem('current_site_name', preferred.name);
          }
        }
      } catch (e) {
        setHasSiteAccess(false);
        console.warn('登录后获取站点列表失败:', e);
      }
      
      return { success: true };
    } catch (error: any) {
      return { success: false, message: error.message || '登录失败' };
    }
  }, []);

  const register = useCallback(async (username: string, password: string, nickname: string) => {
    try {
      const response = await authApi.register({ username, password, nickname });
      
      // 保存 Token
      localStorage.setItem('token', response.token);
      
      // 保存用户信息
      const userInfo = response.user;
      // 根据 role 判断是否是管理员: admin(平台管理员), tenant(代理商), site_admin(站点管理员)
      const isAdminByRole = ['admin', 'tenant', 'site_admin'].includes(userInfo.role);
      const baseUser = {
        id: userInfo.id,
        username: userInfo.username,
        nickname: userInfo.nickname,
        avatar: userInfo.avatar || '',
        phone: userInfo.phone,
        wechat: userInfo.wechat,
        isAdmin: isAdminByRole || userInfo.is_admin,
        isVip: userInfo.is_vip,
        role: userInfo.role,
        created_at: userInfo.created_at,
        email: userInfo.email,
      };
      // 透传所有来自 user_meta 的自定义字段
      const knownKeys = ['id', 'username', 'nickname', 'avatar', 'phone', 'wechat', 'is_admin', 'is_vip', 'role', 'created_at', 'email'];
      const customFields: Record<string, string> = {};
      for (const [key, value] of Object.entries(userInfo)) {
        if (!knownKeys.includes(key) && typeof value === 'string') {
          customFields[key] = value;
        }
      }
      const user: User = { ...baseUser, ...customFields };
      
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      setHasSiteAccess(false);
      
      return { success: true };
    } catch (error: any) {
      return { success: false, message: error.message || '注册失败' };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('current_site_id');
    localStorage.removeItem('current_site_name');
    setUser(null);
    setHasSiteAccess(false);
  }, []);

  const resetPassword = useCallback(async (username: string) => {
    try {
      await authApi.resetPassword(username);
      return { success: true };
    } catch (error: any) {
      return { success: false, message: error.message || '密码重置失败' };
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoggedIn: !!user,
        isAdmin: !!user?.isAdmin,
        isLoading,
        hasSiteAccess,
        login,
        register,
        logout,
        resetPassword,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
